import React from 'react';
import { Link } from 'react-router-dom';
import Card from '../Card';
import StatTile from '../StatTile';
import { CardSkeleton, Icon, Tabs, cx } from '../ui';
import { formatCost, formatNumber, formatPercent } from '../charts';

// The Rankings tab: one leaderboard, where every user carries all four figures and one of
// them ranks. Four separate lists could not show that the biggest token user is cheap, or
// that the top spender barely writes. Data: GET /api/dashboard/leaderboard, under the
// page's filters, fetched only while this tab is open.

export const LEADERBOARD_METRICS = [
  { key: 'cost', label: 'Cost', noun: 'cost', format: formatCost },
  { key: 'tokens', label: 'Tokens', noun: 'tokens', format: formatNumber },
  { key: 'messages', label: 'Messages', noun: 'messages', format: formatNumber },
  { key: 'tool_calls', label: 'Tool calls', noun: 'tool calls', format: formatNumber },
];

// Medal colours for the podium, then the accent tint for the top ten, then neutral.
const rankClass = (rank) => (rank <= 3 ? `rank-${rank}` : rank <= 10 ? 'rank-top' : 'rank-rest');

// How far a user moved since the previous window of the same length.
function Movement({ rank, prevRank }) {
  if (prevRank == null) {
    return <span className="lb-move is-new" title="No activity in the previous window">New</span>;
  }
  const d = prevRank - rank;
  if (d === 0) {
    return (
      <span className="lb-move is-same" title={`Rank ${prevRank} in the previous window too`}>
        <Icon name="minus" size={12} />
        <span className="sr-only">Same rank</span>
      </span>
    );
  }
  return (
    <span className={cx('lb-move', d > 0 ? 'is-up' : 'is-down')}
          title={`Rank ${prevRank} in the previous window`}>
      <Icon name={d > 0 ? 'arrowUp' : 'arrowDown'} size={12} strokeWidth={2.25} />
      {Math.abs(d)}
      <span className="sr-only">{d > 0 ? ' places up' : ' places down'}</span>
    </span>
  );
}

function change(now, before) {
  if (before == null || before <= 0) return null;
  return ((now - before) / before) * 100;
}

const signed = (pct) => `${pct > 0 ? '+' : ''}${formatPercent(pct, 0)}`;

export default function Leaderboard({
  board, sort, onSort, error, onRetry, narrowedMessages,
}) {
  const metric = LEADERBOARD_METRICS.find(m => m.key === sort) || LEADERBOARD_METRICS[0];

  if (!board) {
    if (error) {
      return (
        <div className="card load-failed" role="alert">
          <Icon name="alertCircle" size={20} />
          <div className="grow">
            <h2 className="m-0 text-md">Could not load the rankings</h2>
            <p className="text-muted mt-1 mb-0">{error}</p>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Try again</button>
        </div>
      );
    }
    return (
      <div className="dashboard-grid" role="status" aria-busy="true">
        <span className="sr-only">Loading…</span>
        {[0, 1, 2, 3].map(i => <div key={i} className="dashboard-tile-3"><CardSkeleton lines={2} /></div>)}
        <div className="dashboard-tile-12"><CardSkeleton lines={8} /></div>
      </div>
    );
  }

  const rows = board.rows || [];
  const totals = board.totals || {};
  const previous = board.previous || {};
  const total = totals[metric.key] || 0;
  const top10 = rows.slice(0, 10).reduce((s, r) => s + (r[metric.key] || 0), 0);
  const top10Share = total > 0 ? (top10 / total) * 100 : null;
  const totalChange = previous.available ? change(total, previous.total) : null;
  // The rows on screen belong to another sort until the new request lands.
  const stale = board.sort !== metric.key;
  const compared = Boolean(previous.available);

  return (
    <>
      {error && (
        <div role="status" className="notice notice-warn">
          <div className="notice-body">
            <strong>Showing older rankings.</strong> The last refresh failed ({error}).
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>
        </div>
      )}

      <div className="dashboard-grid">
        <StatTile span={3} icon="users" label="Users ranked"
                  value={formatNumber(totals.ranked_users || 0)}
                  sub={`of ${formatNumber(totals.active_users || 0)} active in this window`}
                  help={`Users with any ${metric.noun} in this window. Active users are those with any usage, message or tool call.`} />
        <StatTile span={3} icon="analytics" label={`Total ${metric.noun}`}
                  value={metric.format(total)}
                  sub={totalChange == null
                    ? (compared ? 'none in the previous window' : 'no earlier window to compare')
                    : `${signed(totalChange)} on the previous window`}
                  help="The whole fleet in this window, not only the users listed. The comparison is with the window of the same length just before this one." />
        <StatTile span={3} icon="pieChart" label="Top 10 share"
                  value={top10Share == null ? '0%' : formatPercent(top10Share, 0)}
                  sub={`of all ${metric.noun} in this window`}
                  help={`How concentrated ${metric.noun} is: the part of the window's total that its ten biggest users account for.`} />
        <StatTile span={3} icon="gauge" label="Median user"
                  value={totals.median == null ? metric.format(0) : metric.format(totals.median)}
                  sub={rows.length ? `the top user: ${metric.format(rows[0][metric.key] || 0)}` : 'nobody ranked yet'}
                  help={`Half the users ranked have less ${metric.noun} than this, half have more.`} />
      </div>

      <div className="dashboard-grid">
        <Card title="Leaderboard"
              subtitle={`top ${rows.length} by ${metric.noun}`}
              help={`Every user with activity in the window, ranked by ${metric.noun}, with their other figures beside it. Share is their part of the whole fleet's ${metric.noun}. Change compares their rank with the previous window of the same length, when the activity logs still hold it${previous.retention_days ? ` (they keep ${previous.retention_days} days)` : ''}.`}
              span={12}>
          {/* In the body rather than the card's actions: four tabs beside the title do not
              fit a phone, and the rank-by choice must stay reachable with nobody ranked. */}
          <div className="tile-toolbar">
            <span className="filter-bar-label" aria-hidden="true">Rank by</span>
            <Tabs size="sm" label="Rank users by" value={metric.key} onChange={onSort}
                  tabs={LEADERBOARD_METRICS.map(m => ({ value: m.key, label: m.label }))} />
          </div>
          {rows.length === 0 ? (
            <div className="tile-state tile-empty">{`Nobody has any ${metric.noun} in this window`}</div>
          ) : (
          <div className={cx('table-wrap table-scroll lb-scroll', stale && 'is-stale')}
               aria-busy={stale || undefined}>
            <table className="table table-compact lb-table">
              <caption className="sr-only">
                {`Users ranked by ${metric.noun}, with their cost, tokens, messages and tool calls`}
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="num lb-rank">#</th>
                  <th scope="col">User</th>
                  {LEADERBOARD_METRICS.map(m => (
                    <th key={m.key} scope="col"
                        className={cx('num', m.key === metric.key && 'is-sorted')}
                        aria-sort={m.key === metric.key ? 'descending' : undefined}>
                      {m.label}
                    </th>
                  ))}
                  <th scope="col" className="lb-share-col">Share</th>
                  {compared && <th scope="col" className="num">Change</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const value = r[metric.key] || 0;
                  const share = total > 0 ? (value / total) * 100 : 0;
                  const moved = change(value, r.prev_value);
                  return (
                    <tr key={r.user_id}>
                      <td className="num lb-rank">
                        <span className={`rank-badge ${rankClass(r.rank)}`}>{r.rank}</span>
                      </td>
                      <td className="lb-user">
                        {/* Every role that sees the Dashboard can open a user's page. */}
                        <Link to={`/users/${r.user_id}`} className="cell-primary">{r.username}</Link>
                        <span className="cell-sub">{r.tier_name || 'No tier'}</span>
                      </td>
                      {LEADERBOARD_METRICS.map(m => (
                        <td key={m.key} className={cx('num', m.key === metric.key && 'is-sorted')}>
                          {m.format(r[m.key] || 0)}
                        </td>
                      ))}
                      <td className="lb-share-col">
                        <span className="lb-share">
                          {/* At least a sliver, so a small share still reads as a share. */}
                          <span className="lb-share-track" aria-hidden="true">
                            <span className="lb-share-fill" style={{ width: `${Math.max(share, 1.5)}%` }} />
                          </span>
                          <span className="lb-share-pct">{formatPercent(share, share < 10 ? 1 : 0)}</span>
                        </span>
                      </td>
                      {compared && (
                        <td className="num">
                          <Movement rank={r.rank} prevRank={r.prev_rank} />
                          {moved != null && <span className="cell-sub">{signed(moved)}</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {narrowedMessages && (
            <p className="text-sm text-muted mt-3 mb-0">
              Messages are counted for every model and source: the message log records neither.
            </p>
          )}
          {!compared && previous.reason === 'retention' && (
            <p className="text-sm text-muted mt-3 mb-0">
              No change column: the previous window of the same length is older than the
              {' '}{previous.retention_days}{' '}days the activity logs keep.
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
